import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface ArticleCardProps {
  id: string;
  title: string;
  excerpt: string;
  coverImage: string;
  category: string;
  author: string;
  authorAvatar: string;
  publishedAt: string;
  readCount: number;
  tags: string[];
}

function formatNumber(num: number): string {
  if (num >= 10000) return (num / 10000).toFixed(1) + "万";
  return num.toString();
}

export default function ArticleCard({
  id,
  title,
  excerpt,
  coverImage,
  category,
  author,
  authorAvatar,
  publishedAt,
  readCount,
  tags,
}: ArticleCardProps) {
  const { t } = useTranslation();

  return (
    <Link
      to={`/article/${id}`}
      className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
    >
      <div className="w-full sm:w-48 flex-shrink-0 aspect-video sm:aspect-[4/3] rounded-md overflow-hidden">
        <img
          src={coverImage}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
      </div>
      <div className="flex-1 min-w-0 flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
              {category}
            </span>
            <span className="text-xs text-foreground-400">{publishedAt}</span>
          </div>
          <h3 className="text-base font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors">
            {title}
          </h3>
          <p className="mt-1.5 text-sm text-foreground-500 line-clamp-2">{excerpt}</p>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={authorAvatar} alt={author} className="w-6 h-6 rounded-full object-cover" loading="lazy" />
            <span className="text-xs text-foreground-500">{author}</span>
            <span className="text-xs text-foreground-400">· {formatNumber(readCount)}{t("article.readCount")}</span>
          </div>
          <div className="hidden sm:flex items-center gap-1.5">
            {tags.slice(0, 3).map((tag) => (
              <span key={tag} className="px-2 py-0.5 rounded text-xs bg-secondary-100 text-secondary-700 whitespace-nowrap">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </Link>
  );
}